import { CircularProgress } from '@mui/material';
import { ReactElement, useEffect, useRef, useState } from 'react';

import {
  Error as FailedIcon,
  CheckCircle as SuccessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useMovableElement } from './model';
import { useStyles } from './styles';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'intl';
import {
  Progress,
  UploadProgresFile,
  UploadProgresStatus,
  useUploadProgressStore,
} from 'store/uploadProgress';
import { useUploadProgressController } from './useUploadProgressController';
import { ButtonIcon, ExpandChevron } from 'components/general';
import { Color } from 'shared/utils/colors';

export function UploadProgress(): ReactElement {
  const classes = useStyles();
  const canSetVisibleRef = useRef(true);
  const { clear } = useUploadProgressController();
  const files = useUploadProgressStore.use.files();
  const [isVisible, setIsVisible] = useState(Boolean(files.length));
  const [isOpen, setOpen] = useState(true);

  const {
    position,
    mouseDownHandler,
    touchStartHandler,
    moveEnd,
    mouseMoveHandler,
    touchMoveHandler,
    reset,
  } = useMovableElement();

  useEffect(() => {
    const visible = Boolean(files.length);
    if (canSetVisibleRef.current && visible) {
      setIsVisible((prev) => {
        if (visible !== prev) {
          canSetVisibleRef.current = false;
        }

        if (visible) {
          setOpen(true);
        }
        return visible;
      });
    }
  }, [files]);

  // react 18 types
  // @ts-ignore
  return createPortal(
    <div
      className={classes.root}
      style={{
        display: isVisible ? 'inline-flex' : 'none',
        bottom: position ? 'unset' : '60px',
        right: position ? 'unset' : '60px',
        left: position ? `${position.x}px` : 'unset',
        top: position ? `${position.y}px` : 'unset',
      }}
    >
      <h3 className={classes.header}>
        <div className={classes.actionsContainer}>
          <div
            onMouseDown={mouseDownHandler}
            onMouseMove={mouseMoveHandler}
            onMouseUp={moveEnd}
            onMouseOut={moveEnd}
            onTouchMove={touchMoveHandler}
            onTouchStart={touchStartHandler}
            onTouchEnd={moveEnd}
            className={classes.dragger}
          ></div>
          <ButtonIcon onClick={() => setOpen((prev) => !prev)}>
            {/* Panel is docked at the bottom and expands upward, so the chevron
                points up when collapsed and down when open (inverted vs a
                normal top-down accordion). */}
            <ExpandChevron expanded={!isOpen} htmlColor={Color.grayEee} />
          </ButtonIcon>
          <ButtonIcon
            onClick={() =>
              clear().then(() => {
                canSetVisibleRef.current = true;
                setIsVisible(false);
                reset();
              })
            }
          >
            <CloseIcon htmlColor='#EEEEEE' />
          </ButtonIcon>
        </div>
      </h3>
      <div
        className={classes.listContainer}
        style={{ height: isOpen ? 'min(40vh, 260px)' : 0 }}
      >
        {files.map((file) => {
          return (
            <div
              key={`${file.uniqueFileId}-${file.status}`}
              className={classes.listElement}
            >
              <span>
                <FileTitle file={file} />
              </span>
              <Icon status={file.status} />
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

function FileTitle({ file }: { file: UploadProgresFile }): ReactElement {
  if (file.titleTranslationKey) {
    return <FormattedMessage id={file.titleTranslationKey} />;
  }
  return <>{file.title}</>;
}

function Icon({ status }: { status: UploadProgresStatus }): ReactElement {
  switch (status) {
    case Progress.FAILED:
      return <FailedIcon htmlColor='red' />;
    case Progress.PENDING:
      return <CircularProgress size={24} />;
    case Progress.UPLOADED:
      return <SuccessIcon htmlColor='green' />;
  }
}
